import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  makeStyles
} from "@fluentui/react-components";
import { FormattedMessage, useIntl } from "react-intl";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { fetchReticulumAuthenticated } from "../../utils/phoenix-utils";

const useStyles = makeStyles({
  textField: {
    width: "100%"
  }
});

const AVATARS_API = "/api/v1/avatars";

export function RenameAvatarDialog({ avatarId, open, onOpenChange, onSave }) {
  const intl = useIntl();
  const styles = useStyles();

  const [avatar, setAvatar] = useState(null);
  const [originalAvatarName, setOriginalAvatarName] = useState("");

  const fetchAvatar = useCallback(async avatarId => {
    const { avatars } = await fetchReticulumAuthenticated(`${AVATARS_API}/${avatarId}`);
    return avatars[0];
  }, []);

  useEffect(() => {
    if (avatarId) {
      fetchAvatar(avatarId).then(avatar => {
        setAvatar(avatar);
        setOriginalAvatarName(avatar?.name || "");
      });
    }
  }, [avatarId, fetchAvatar]);

  const createOrUpdateAvatar = async avatar => {
    const { avatars } = await fetchReticulumAuthenticated(
      avatar.avatar_id ? `${AVATARS_API}/${avatar.avatar_id}` : AVATARS_API,
      avatar.avatar_id ? "PUT" : "POST",
      {
        avatar: {
          ...avatar,
          creatorAttribution: ""
        }
      }
    );
    return avatars[0];
  };

  const handleChangeName = useCallback(
    e => {
      setAvatar({ ...avatar, name: e.target.value });
    },
    [avatar]
  );

  const handleSave = async () => {
    const avatarToCreate = {
      ...avatar,
      attributions: {
        creator: avatar.creatorAttribution
      },
      files: {
        base_map: null,
        emissive_map: null,
        normal_map: null,
        orm_map: null
      }
    };

    const newAvatar = await createOrUpdateAvatar(avatarToCreate);
    onOpenChange(null, {
      open: false
    });
    onSave(newAvatar);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={intl.formatMessage({ id: "room-settings-sidebar.close-button", defaultMessage: "Close" })}
                icon={<Dismiss24Regular />}
                onClick={() => onOpenChange(null, { open: false })}
              />
            }
          >
            <FormattedMessage id="avatar-editor.rename-avatar-button" defaultMessage="Rename avatar" />
          </DialogTitle>
          <DialogContent>
            <Input
              appearance="filled-darker"
              required
              value={avatar?.name || ""}
              onChange={handleChangeName}
              placeholder={intl.formatMessage({
                id: "avatar-editor.field.avatar-name",
                defaultMessage: "Avatar name"
              })}
              className={styles.textField}
            />
          </DialogContent>
          <DialogActions>
            <Button appearance="outline" onClick={() => onOpenChange(null, { open: false })}>
              <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={!avatar?.name || avatar.name === originalAvatarName}
            >
              <FormattedMessage id="avatar-editor.submit-button.save" defaultMessage="Save" />
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

RenameAvatarDialog.propTypes = {
  //   onClose: PropTypes.func.isRequired,
  avatarId: PropTypes.string,
  //   onSave: PropTypes.func,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onSave: PropTypes.func
};
